import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { careerService, internshipService } from '../services/api';
import { useTranslation } from 'react-i18next';
import SkeletonLoader from '../components/ui/SkeletonLoader';

function CareerCoachPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [recommendations, setRecommendations] = useState([]);
    const [roadmap, setRoadmap] = useState([]);
    const [applications, setApplications] = useState([]);
    const [targetRole, setTargetRole] = useState('');
    const [roadmapMode, setRoadmapMode] = useState('target');
    const [loading, setLoading] = useState({
        recommendations: false,
        roadmap: false,
    });
    const [error, setError] = useState('');

    useEffect(() => {
        internshipService.list()
            .then(res => setApplications(res.data))
            .catch(() => {});
    }, []);

    const fetchRecommendations = async () => {
        setLoading(prev => ({ ...prev, recommendations: true }));
        setError('');
        try {
            const response = await careerService.getRecommendations();
            setRecommendations(response.data.recommendations);
        } catch (err) {
            setError(t('career.errors.recommendations_failed'));
        } finally {
            setLoading(prev => ({ ...prev, recommendations: false }));
        }
    };

    const fetchRoadmap = async () => {
        if (!targetRole.trim()) {
            setError(t('career.errors.target_role_required'));
            return;
        }
        setLoading(prev => ({ ...prev, roadmap: true }));
        setError('');
        try {
            const response = await careerService.getRoadmap(targetRole.trim(), roadmapMode);
            setRoadmap(response.data.roadmap);
        } catch (err) {
            setError(t('career.errors.roadmap_failed'));
        } finally {
            setLoading(prev => ({ ...prev, roadmap: false }));
        }
    };

    return (
        <Layout>
            <div className="page-container">
                <h1>{t('career.title')}</h1>
                <p className="subtitle">{t('career.subtitle_long')}</p>

                {error && <div className="error-message">{error}</div>}

                {/* Career Recommendations */}
                <div className="card">
                    <div className="card-header">
                        <div>
                            <h2>{t('career.path_recommendations')}</h2>
                            <p>{t('career.based_on_profile')}</p>
                        </div>
                        <button
                            onClick={fetchRecommendations}
                            disabled={loading.recommendations}
                            className="btn-primary"
                        >
                            {loading.recommendations ? t('resume.analysing') : `🤖 ${t('career.get_recommendations')}`}
                        </button>
                    </div>

                    {recommendations.length > 0 && (
                        <div className="recommendations-grid">
                            {recommendations.map((rec, i) => (
                                <div key={i} className="recommendation-card">
                                    <h3>{rec.title}</h3>
                                    <p>{rec.description}</p>
                                    <div className="rec-meta">
                                        <span className={`growth-badge growth-${rec.growth_potential?.toLowerCase()}`}>
                                            {rec.growth_potential} {t('career.growth')}
                                        </span>
                                        <span className="salary">{rec.avg_salary_range}</span>
                                    </div>
                                    {rec.required_skills?.length > 0 && (
                                        <div className="skills-list">
                                            {rec.required_skills.map((skill, j) => (
                                                <span key={j} className="skill-tag">{skill}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Interview Simulator */}
                <div className="card">
                    <div className="card-header">
                        <div>
                            <h2>{t('career.interview_simulator')}</h2>
                            <p>{t('career.interview_simulator_subtitle')}</p>
                        </div>
                        <button
                            onClick={() => navigate('/interview')}
                            className="btn-primary"
                        >
                            🎤 {t('career.start_interview')}
                        </button>
                    </div>

                    <div className="interview-info-box">
                        <div className="interview-info-item">
                            <span>🎯</span>
                            <span>{t('career.interview_info_1')}</span>
                        </div>
                        <div className="interview-info-item">
                            <span>🤖</span>
                            <span>{t('career.interview_info_2')}</span>
                        </div>
                        <div className="interview-info-item">
                            <span>📊</span>
                            <span>{t('career.interview_info_3')}</span>
                        </div>
                        <div className="interview-info-item">
                            <span>💡</span>
                            <span>{t('career.interview_info_4')}</span>
                        </div>
                    </div>
                </div>

                {/* Learning Roadmap */}
                <div className="card">
                    <h2>{t('career.roadmap')}</h2>
                    <p>{t('career.roadmap_subtitle')}</p>

                    <div className="roadmap-mode-toggle" style={{ marginTop: '1rem' }}>
                        <label className={`roadmap-mode-option ${roadmapMode === 'target' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="roadmap_mode"
                                checked={roadmapMode === 'target'}
                                onChange={() => setRoadmapMode('target')}
                            />
                            <div>
                                <strong>{t('career.target_role_mode')}</strong>
                                <p>{t('career.target_role_mode_desc')}</p>
                            </div>
                        </label>
                        <label className={`roadmap-mode-option ${roadmapMode === 'gap' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="roadmap_mode"
                                checked={roadmapMode === 'gap'}
                                onChange={() => setRoadmapMode('gap')}
                            />
                            <div>
                                <strong>{t('career.gap_mode')}</strong>
                                <p>{t('career.gap_mode_desc')}</p>
                            </div>
                        </label>
                    </div>

                    <div className="input-row" style={{ marginTop: '1rem' }}>
                        <input
                            type="text"
                            value={targetRole}
                            onChange={e => setTargetRole(e.target.value)}
                            placeholder={t('career.target_role_placeholder')}
                        />
                        <button
                            onClick={fetchRoadmap}
                            disabled={loading.roadmap}
                            className="btn-primary"
                        >
                            {loading.roadmap ? t('career.loading') : `🗺️ ${t('career.generate_roadmap')}`}
                        </button>
                    </div>

                    {roadmap.length > 0 && (
                        <div className="roadmap-list">
                            {roadmap
                                .sort((a, b) => a.order - b.order)
                                .map((step, i) => (
                                    <div key={i} className="roadmap-step">
                                        <div className="step-number">{step.order}</div>
                                        <div className="step-content">
                                            <h4>{step.skill}</h4>
                                            <p>{step.reason}</p>
                                            <a href={step.resource_url} target="_blank" rel="noreferrer">
                                                {t('career.resource_prefix')} {step.recommended_resource}
                                            </a>
                                            <small>{step.estimated_weeks} {t('career.weeks')}</small>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export default CareerCoachPage;